import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { IGlobalMail } from '../../Interfaces/IGlobalMail';
import { CustomEditor } from '../../core/CustomEditor';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import DownloadIcon from '@mui/icons-material/Download';

import { useGetLegalClientGroups } from '../../hooks/reactQuerys/LegalClientGroups';
import { useSendGlobalMail } from '../../hooks/reactQuerys/GlobalMailQueries';

import {
  GlobalMailInputSchema,
  GlobalMailInput,
} from '../../Interfaces/IGlobalMailInput';
import { customFetcher } from '../../hooks/useFetch';
import { CustomeTextField } from '../../generic/CustomeTextField';

interface IProps {
  currentId: number;
  setCurrentId: Function;
  currentArray: IGlobalMail[];
  setCurrentArray: Function;
  setWasSaved: Function;
  setWasSavedSuccesfully: Function;

  onSendSuccess?: () => void; //für zurück zur anderen Ansicht
}

export const GlobalMailEdit: React.FC<IProps> = props => {
  const isNew = props.currentId === -1;

  const [systemMailOrg, setSystemMailOrg] = useState<IGlobalMail>(() => {
    const found = props.currentArray.find(
      x => x.idGlobalMail === props.currentId
    );
    return (
      found ?? {
        idGlobalMail: -1,
        Title: '',
        Subject: '',
        Body: '',
        AttachmentsData: '[]',
        RecipientsData: '[]',
      }
    );
  });

  const [sendResult, setSendResult] = useState<{
    Correct: number;
    Error: number;
    Total: number;
  } | null>(null);


  const [editorKey, setEditorKey] = useState(0);

  const [systemMailObject, setSystemMailObject] = useState(systemMailOrg);

  const [sendToAll, setSendToAll] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);

  const [isOpenConfirmSend, setIsOpenConfirmSend] = useState(false);
  const [isOpenShowResult, setIsOpenShowResult] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openRecipientsDialog, setOpenRecipientsDialog] = useState(false);

  const { legalClientGroups } = useGetLegalClientGroups();
  const { sendGlobalMail, isPendingSendGlobalMail } = useSendGlobalMail();

  const fetchDocumentDirect = async (id: number) =>
    await customFetcher({ endpoint: `/document/${id}` });

  const downloadDocument = async (idDocument: number, fileName: string) => {
    const doc = await fetchDocumentDirect(idDocument);

    if (!doc?.Data) {
      alert('Dokument konnte nicht geladen werden.');
      return;
    }

    const byteCharacters = atob(doc.Data);
    const byteNumbers = Array.from(byteCharacters).map(c => c.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  };

  const [attachments, setAttachments] = useState<
    { FileName: string; Data: string }[]
  >([]);

  const isDisabled = () =>
    !systemMailObject.Title ||
    !systemMailObject.Subject ||
    !systemMailObject.Body;

  const handleSend = async () => {
    const payload: GlobalMailInput = {
      Title: systemMailObject.Title,
      Subject: systemMailObject.Subject,
      Body: systemMailObject.Body,
      GroupIds: sendToAll ? undefined : selectedGroups,
      Attachments: attachments.length > 0 ? attachments : undefined,
    };

    const parsed = GlobalMailInputSchema.safeParse(payload);
    if (!parsed.success) {
      alert('Bitte füllen Sie alle Felder korrekt aus.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await sendGlobalMail(parsed.data);

      setSendResult({
        Correct: result.Correct,
        Error: result.Error,
        Total: result.Total,
      });

      const empty: IGlobalMail = {
        idGlobalMail: -1,
        Title: '',
        Subject: '',
        Body: '',
        AttachmentsData: '[]',
        RecipientsData: '[]',
      };

      setSystemMailOrg(empty);
      setSystemMailObject(empty);
      setSelectedGroups([]);
      setSendToAll(true);
      setAttachments([]);
      setEditorKey(prev => prev + 1);

      setIsOpenConfirmSend(false);
      setIsOpenShowResult(true);

    } finally {
      setIsLoading(false);
    }
  };

  if (!systemMailOrg || !systemMailObject) {
    return <Alert severity='error'>Fehler!</Alert>;
  }

  const recipients = React.useMemo(() => {
    try {
      return JSON.parse(systemMailOrg.RecipientsData || '[]');
    } catch {
      return [];
    }
  }, [systemMailOrg]);

  const sentToGroups = recipients.some(
    (r: any) => r.Groups && r.Groups.length > 0
  );

  const parsedAttachments = React.useMemo(() => {
    try {
      return JSON.parse(systemMailOrg.AttachmentsData || '[]');
    } catch {
      return [];
    }
  }, [systemMailOrg]);

  return (
    <>
      {/* Ergebnis-Dialog */}
      <Dialog
        open={isOpenShowResult}
        onClose={() => setIsOpenShowResult(false)}
      >
        <DialogTitle>E-Mails werden versendet!</DialogTitle>
        <DialogContent>
          <Alert severity='success'>
            Ihre E-Mail(s) werden versendet!
            {sendResult && (
              <table>
                <tbody>
                  <tr>
                    <td>
                      <b>Erfolgreich</b>
                    </td>
                    <td>{sendResult.Correct}</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Fehlerhaft</b>
                    </td>
                    <td>{sendResult.Error}</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Total</b>
                    </td>
                    <td>
                      <b>{sendResult.Total}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            onClick={() => {
              setIsOpenShowResult(false);
              props.onSendSuccess?.();
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lade-Indikator */}
      <Dialog open={isLoading} onClose={() => {}} maxWidth='sm' fullWidth>
        <DialogContent>
          <LoadingSpinner />
        </DialogContent>
      </Dialog>

      {/* Bestätigungsdialog */}
      <Dialog
        open={isOpenConfirmSend}
        onClose={() => setIsOpenConfirmSend(false)}
      >
        <DialogTitle>Senden bestätigen</DialogTitle>
        <DialogContent>
          {sendToAll
            ? 'Sind Sie sicher, dass Sie an alle Mandanten senden möchten?'
            : 'Sind Sie sicher, dass Sie an die ausgewählten Gruppen senden möchten?'}
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => setIsOpenConfirmSend(false)}
          >
            Abbruch
          </Button>
          <Button
            variant='contained'
            onClick={handleSend}
            disabled={isDisabled() || isPendingSendGlobalMail}
          >
            Jetzt senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: Empfängerliste */}
      <Dialog
        open={openRecipientsDialog}
        onClose={() => setOpenRecipientsDialog(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Empfänger</DialogTitle>
        <DialogContent dividers>
          <ul style={{ paddingLeft: 16 }}>
            {recipients.map((r: any, index: number) => (
              <li key={index} style={{ marginBottom: 8 }}>
                <b>{r.Email}</b>
                {r.Groups.length > 0 && (
                  <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                    Gruppen:{' '}
                    {r.Groups.map(
                      (gid: number) =>
                        legalClientGroups?.find(
                          g => g.idLegalClientGroup === gid
                        )?.Name ?? gid
                    ).join(', ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenRecipientsDialog(false)}
            variant='contained'
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* HEADER */}
      <Typography variant='h5'>
        {systemMailOrg.Title}

        {isNew && (
          <>
            <Button
              variant='contained'
              sx={{ float: 'right', ml: 2 }}
              onClick={() => setIsOpenConfirmSend(true)}
              disabled={isDisabled()}
            >
              Senden
            </Button>
            <Button
              variant='outlined'
              sx={{ float: 'right' }}
              onClick={() => props.setCurrentId(null)}
            >
              Abbruch
            </Button>
          </>
        )}
      </Typography>

      <Box sx={{ mt: 2 }} />

      {/* INFOBOX FÜR BEREITS VERSENDETE MAILS */}
      {!isNew && (
        <Alert severity='info' sx={{ mt: 2 }}>
          {sentToGroups ? (
            <>
              <b>Gesendet an bestimmte Gruppen</b>
              <div>
                Gruppen:{' '}
                {Array.from(new Set(recipients.flatMap((r: any) => r.Groups)))
                  .map(
                    gid =>
                      legalClientGroups?.find(g => g.idLegalClientGroup === gid)
                        ?.Name ?? `Gruppe ${gid}`
                  )
                  .join(', ')}
              </div>
            </>
          ) : (
            <b>Gesendet an alle Mandanten</b>
          )}

          <div style={{ marginTop: 8 }}>
            Gesamt: <b>{recipients.length}</b> Empfänger
          </div>

          <Button
            size='small'
            variant='outlined'
            sx={{ mt: 1 }}
            onClick={() => setOpenRecipientsDialog(true)}
          >
            Empfänger anzeigen
          </Button>
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Empfänger-Auswahl */}
        {isNew && (
          <Grid item xs={12}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Empfänger auswählen
            </Typography>

            <Box display='flex' gap={2}>
              <Button
                variant={sendToAll ? 'contained' : 'outlined'}
                onClick={() => setSendToAll(true)}
              >
                An alle Mandanten
              </Button>

              <Button
                variant={!sendToAll ? 'contained' : 'outlined'}
                onClick={() => setSendToAll(false)}
              >
                An Gruppen
              </Button>
            </Box>
          </Grid>
        )}

        {/* Gruppen-Auswahl */}
        {isNew && !sendToAll && (
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={legalClientGroups}
              disableCloseOnSelect
              getOptionLabel={g => g.Name}
              value={legalClientGroups.filter(g =>
                selectedGroups.includes(g.idLegalClientGroup)
              )}
              onChange={(_, values) =>
                setSelectedGroups(values.map(g => g.idLegalClientGroup))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return (
                    <Chip
                      key={index}
                      label={option.Name}
                      size='small'
                      {...rest}
                      sx={{
                        backgroundColor: option.ColorHex ?? '#666',
                        color: 'white',
                      }}
                    />
                  );
                })
              }
              renderInput={params => (
                <TextField {...params} label='Gruppen auswählen' />
              )}
            />
          </Grid>
        )}

        {/* Titel */}
        <Grid item sm={4}>
          <TextField
            label='Titel (nur intern)'
            size='small'
            value={systemMailObject.Title}
            onChange={e =>
              isNew &&
              setSystemMailObject({
                ...systemMailObject,
                Title: e.target.value,
              })
            }
            disabled={!isNew}
            fullWidth
          />
        </Grid>

        {/* Betreff */}
        <Grid item sm={8}>
          <CustomeTextField
            label='Betreff'
            attr='Subject'
            object={systemMailObject}
            setObject={setSystemMailObject}
            type='string'
            size='small'
            disabled={!isNew}
            maxLength='md'
          />
        </Grid>

        {/* Editor */}
        <CustomEditor
          key={`editor-${editorKey}`}
          idTextModuleCategory={1}
          txt={systemMailObject.Body}
          dictonaryIdsArray={[1, 3]}
          setTxt={(txt: string) =>
            isNew &&
            setSystemMailObject({
              ...systemMailObject,
              Body: txt,
            })
          }
        />

        {/* ANHÄNGE */}
        <Grid item xs={12}>
          {isNew ? (
            <>
              <Button variant='outlined' component='label'>
                Datei anhängen
                <input
                  type='file'
                  hidden
                  multiple
                  onChange={async e => {
                    const files = e.target.files;
                    if (!files) return;

                    const newFiles: any[] = [];

                    for (let i = 0; i < files.length; i++) {
                      const file = files[i];
                      const base64 = await new Promise<string>(resolve => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as string);
                        reader.readAsDataURL(file);
                      });

                      const pure = base64.split(',')[1];

                      newFiles.push({
                        FileName: file.name,
                        Data: pure,
                      });
                    }

                    setAttachments(prev => [...prev, ...newFiles]);
                  }}
                />
              </Button>

              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {attachments.map((att, i) => (
                  <Chip
                    key={i}
                    label={att.FileName}
                    onDelete={() =>
                      setAttachments(prev =>
                        prev.filter((_, index) => index !== i)
                      )
                    }
                  />
                ))}
              </Box>
            </>
          ) : (
            <>
              <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                Anhänge
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {parsedAttachments.map((a: any, i: number) => (
                  <Chip
                    key={i}
                    label={a.FileName}
                    onDelete={() => downloadDocument(a.idDocument, a.FileName)}
                    deleteIcon={
                      <DownloadIcon
                        onClick={e => {
                          e.stopPropagation();
                          downloadDocument(a.idDocument, a.FileName);
                        }}
                      />
                    }
                    sx={{
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};
